import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { decodeFilterItem, filterItems, filterParam } from '@forecastify/shared';
import { useSession } from '../session';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 3 Nov 2026 · A LIST REMEMBERS ITS FILTERS, SEARCH AND SORT — in the       │
 * │ browser, per workspace, per member (`decisions.md` R-63).                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner: *"every time I come back to a list, my filters are gone."* Each list keeps
 * one object in `localStorage` under `forecastify.list.<workspaceId>.<memberId>.<listKey>` and
 * reads it back on mount. The rules, decided once here rather than per screen:
 *
 *  1. ⚠️⚠️ **AN ADDRESS THAT CARRIES ITS OWN FILTER WINS FOR THAT VISIT — AND DOES NOT OVERWRITE
 *     THE STORED SET.** A link from a contract to its time logs (`?contractId=`) is a question
 *     about that contract, not a new default. The screen passes `transient: true`; nothing is
 *     restored, and nothing it does is saved — until (2).
 *  2. **A preset or saved-view click overwrites it** (`overwrite`), and ends the transient visit.
 *  3. **"Szűrők törlése" clears the stored set** (`clear`).
 *  4. **Stale ids are pruned once options load** (`pruneIds`) — a project archived since, a
 *     person who left. Without it a remembered filter would match nothing, forever, invisibly.
 *
 * ⚠️ **Storage is wrapped in try/catch everywhere**, the way `rememberTheme` is: a private window
 * or blocked site data THROWS on access, and a remembered filter is not worth a blank screen.
 * ⚠️ **Never state that must persist reliably** — it is a per-browser convenience.
 */

export type Remembered = Readonly<Record<string, unknown>>;

const PREFIX = 'forecastify.list';

export function listStorageKey(workspaceId: string, memberId: string, listKey: string): string {
  return `${PREFIX}.${workspaceId}.${memberId}.${listKey}`;
}

export function readRemembered(key: string): Remembered | null {
  try {
    const raw = globalThis.localStorage.getItem(key);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Remembered)
      : null;
  } catch {
    return null;
  }
}

export function writeRemembered(key: string, value: Remembered): void {
  try {
    globalThis.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Nothing remembered — the next visit starts from the defaults. That is the whole cost. */
  }
}

export function forgetRemembered(key: string): void {
  try {
    globalThis.localStorage.removeItem(key);
  } catch {
    /* As above. */
  }
}

/** A stored string, or `fallback` when absent or of another type. */
export function rememberedString(
  stored: Remembered | null,
  field: string,
  fallback: string,
): string {
  const value = stored?.[field];
  return typeof value === 'string' ? value : fallback;
}

/** A stored boolean, or `fallback`. */
export function rememberedBoolean(
  stored: Remembered | null,
  field: string,
  fallback: boolean,
): boolean {
  const value = stored?.[field];
  return typeof value === 'boolean' ? value : fallback;
}

/** A stored string map, or `fallback`. Non-string values are dropped. */
export function rememberedStrings(
  stored: Remembered | null,
  field: string,
  fallback: Readonly<Record<string, string>>,
): Readonly<Record<string, string>> {
  const value = stored?.[field];
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return fallback;
  const out: Record<string, string> = { ...fallback };
  for (const [key, one] of Object.entries(value as Record<string, unknown>)) {
    if (typeof one === 'string') out[key] = one;
  }
  return out;
}

/** A remembered sort, item by item — anything malformed is dropped rather than sent. */
export function rememberedSort(
  stored: Remembered | null,
): readonly { readonly key: string; readonly direction: 'asc' | 'desc' }[] {
  const value = stored?.sort;
  if (!Array.isArray(value)) return [];
  return value.filter(
    (one): one is { key: string; direction: 'asc' | 'desc' } =>
      typeof one === 'object' &&
      one !== null &&
      typeof (one as { key?: unknown }).key === 'string' &&
      ((one as { direction?: unknown }).direction === 'asc' ||
        (one as { direction?: unknown }).direction === 'desc'),
  );
}

/**
 * A screen's filter object, field by field from what was stored: a field is taken only when the
 * stored value has the SAME type as its default (an object for an object, never an array for
 * one), so a shape change between releases falls back rather than crashing a screen.
 */
export function mergeRemembered<T extends object>(defaults: T, stored: unknown): T {
  if (typeof stored !== 'object' || stored === null || Array.isArray(stored)) return defaults;
  const source = stored as Record<string, unknown>;
  const out: Record<string, unknown> = { ...(defaults as Record<string, unknown>) };
  for (const [key, fallback] of Object.entries(defaults as Record<string, unknown>)) {
    const value = source[key];
    if (value === undefined || value === null) continue;
    const sameKind =
      typeof fallback === 'object' && fallback !== null
        ? typeof value === 'object' && Array.isArray(value) === Array.isArray(fallback)
        : typeof value === typeof fallback;
    if (sameKind) out[key] = value;
  }
  return out as T;
}

/**
 * ⚠️ **A comma list of ids, minus those no longer offered.** `known` is `null` while the options
 * are still loading — and then NOTHING is pruned, or every remembered id would be dropped in the
 * moment before the list arrived. Returns the value unchanged when nothing was stale, so a caller
 * can compare by identity and skip a state update.
 */
export function pruneIds(value: string, known: ReadonlySet<string> | null): string {
  if (known === null || value === '') return value;
  /* ⚠️ Items compared DECODED — a free-text item (a Jira component) travels escaped
     (`encodeFilterItem`); decoding an id is harmless. */
  const items = filterItems(value);
  const kept = items.filter((one) => known.has(decodeFilterItem(one)));
  return kept.length === items.length ? value : filterParam(kept);
}

export interface ListMemory {
  /** What the last visit left — `null` on a first visit, a transient one, or no workspace. */
  readonly restored: Remembered | null;
  /** Remember these fields (merged into what is stored). A no-op while transient. */
  readonly save: (patch: Remembered) => void;
  /** Rule 2 · a preset or saved view — written even when transient, and ends the transient visit. */
  readonly overwrite: (patch: Remembered) => void;
  /** Rule 3 · forget the set entirely. */
  readonly clear: () => void;
}

/**
 * The memory of one list. `listKey` `undefined` turns it off — `useListQuery`'s callers that never
 * asked for it stay exactly as they were.
 */
export function useListMemory(listKey: string | undefined, transient = false): ListMemory {
  const session = useSession();
  const workspaceId = session.workspaceId;
  /* ⚠️ Read defensively: a screen test's session stand-in often carries no workspace list, and a
     convenience must never be what breaks a screen. */
  const workspaces = session.workspaces as
    readonly { readonly id: string; readonly memberId: string }[] | undefined;
  const memberId = workspaces?.find((one) => one.id === workspaceId)?.memberId ?? null;
  const key =
    listKey === undefined || workspaceId === null || memberId === null
      ? null
      : listStorageKey(workspaceId, memberId, listKey);

  /* ⚠️ Read ONCE, at mount: a list re-reading its own memory as it writes would fight itself. */
  const [restored] = useState<Remembered | null>(() =>
    key === null || transient ? null : readRemembered(key),
  );
  const live = useRef(!transient);

  const save = useCallback(
    (patch: Remembered) => {
      if (key === null || !live.current) return;
      writeRemembered(key, { ...(readRemembered(key) ?? {}), ...patch });
    },
    [key],
  );
  const overwrite = useCallback(
    (patch: Remembered) => {
      if (key === null) return;
      live.current = true;
      writeRemembered(key, { ...(readRemembered(key) ?? {}), ...patch });
    },
    [key],
  );
  const clear = useCallback(() => {
    if (key === null) return;
    /* ⚠️ A clear on a transient visit forgets too — the person asked for nothing, everywhere. */
    live.current = true;
    forgetRemembered(key);
  }, [key]);

  return useMemo(() => ({ restored, save, overwrite, clear }), [restored, save, overwrite, clear]);
}

/**
 * ⚠️ **Writes `snapshot` whenever it changes — skipping the first render**, which only draws the
 * restored (or default) state; writing that back would rewrite the same object for no reason.
 * `snapshot` is compared by its JSON, so a fresh object literal per render is fine.
 */
export function useRememberEffect(memory: ListMemory, snapshot: Remembered): void {
  const encoded = JSON.stringify(snapshot);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    memory.save(JSON.parse(encoded) as Remembered);
  }, [encoded, memory]);
}

/**
 * A drop-in for `useState('')` over one remembered field: restored at mount, saved on change
 * (never on the first render). For screens that hold each filter in its own state.
 */
export function useRememberedString(
  memory: ListMemory,
  field: string,
  fallback = '',
): [string, Dispatch<SetStateAction<string>>] {
  const [value, setValue] = useState(() => rememberedString(memory.restored, field, fallback));
  useRememberEffect(memory, { [field]: value });
  return [value, setValue];
}

/** The same, for a boolean toggle. */
export function useRememberedBoolean(
  memory: ListMemory,
  field: string,
  fallback = false,
): [boolean, Dispatch<SetStateAction<boolean>>] {
  const [value, setValue] = useState(() => rememberedBoolean(memory.restored, field, fallback));
  useRememberEffect(memory, { [field]: value });
  return [value, setValue];
}

/**
 * Rule 3 for a screen whose toolbar clears its filters one setter at a time: when `empty` BECOMES
 * true, forget the set. ⚠️ Never on the first render — a transient visit that simply opens with
 * nothing chosen must not wipe what an earlier visit remembered.
 */
export function useClearWhenEmpty(memory: ListMemory, empty: boolean): void {
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (empty) memory.clear();
  }, [empty, memory]);
}

/**
 * Prune a remembered comma list of ids once its options load (rule 4). `known` `null` = still
 * loading. Calls `set` only when something was actually dropped.
 */
export function usePruneIds(
  value: string,
  known: ReadonlySet<string> | null,
  set: (next: string) => void,
): void {
  useEffect(() => {
    const next = pruneIds(value, known);
    if (next !== value) set(next);
  }, [value, known, set]);
}
